import styled, { css, keyframes } from 'styled-components';
import { Col, FlexView, Row, TextInput } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';
import COLORS from 'constants/colors';

import { CONTEXT_TAG_TONE } from './constants';

import {
  SA_COLORS,
  ThreadContainer,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  TicketCardHead,
} from 'screens/support-shared/styled-comps';

export {
  SA_COLORS,
  ThreadContainer,
  ThreadScroll,
  ThreadInner,
  DateSeparator,
  DateSeparatorLabel,
  TicketCardHead,
};

const SA = SA_COLORS;

// ── Keyframe Animations ────────────────────────────────────────────────
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const typingBounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

export const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ── Layout ─────────────────────────────────────────────────────────────
export const Workspace = styled(FlexView)`
  && {
    display: grid;
    grid-template-columns: 300px 1fr 360px;
    grid-template-rows: 1fr;
    height: 100vh;
    background: ${SA.BG_PRIMARY};
  }
`;

// ── Chips & Badges ─────────────────────────────────────────────────────
export const Chip = styled(FlexView)`
  && {
    display: inline-flex;
    flex-direction: row;
    height: 24px;
    padding: 0 8px;
    border: 1px solid ${SA.BORDER_TERTIARY};
    background: ${SA.BG_SECONDARY};
    font-size: 12px;
    line-height: 24px;
    gap: 6px;
    align-items: center;
    white-space: nowrap;
  }
`;

export const ChipLive = styled(Chip)`
  && {
    background: ${SA.GOLD_50};
    border-color: ${SA.GOLD_200};
    color: ${SA.GOLD_700};
  }
`;

export const IconBtn = styled.button`
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  color: ${SA.TEXT_SECONDARY};
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

// ── Ticket List ────────────────────────────────────────────────────────
export const TicketListContainer = styled(FlexView)`
  && {
    border-right: 1px solid ${SA.BORDER_TERTIARY};
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background: ${SA.BG_PRIMARY};
  }
`;

export const ListHeader = styled(Row)`
  && {
    justify-content: space-between;
    align-items: baseline;
  }
`;

export const SavedFiltersSection = styled(FlexView)`
  && {
    padding: 8px 12px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
    background: ${SA.BG_SECONDARY};
  }
`;

export const FilterDropdownWrap = styled(FlexView)`
  && {
    position: relative;
    flex: 1;
  }
`;

export const FilterDropdownTrigger = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  background: ${SA.BG_PRIMARY};
  font-size: 11px;
  cursor: pointer;
  color: ${SA.TEXT_PRIMARY};

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const FilterDropdownMenu = styled(FlexView)`
  && {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    max-height: 300px;
    overflow: auto;
    z-index: 100;
    animation: ${slideDown} 0.15s ease-out;
  }
`;

export const FilterDropdownItem = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 13px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${SA.TEXT_PRIMARY};

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const FilterDropdownItemActive = styled(FilterDropdownItem)`
  background: ${SA.NEUTRAL_1000};
  color: white;

  &:hover {
    background: ${SA.NEUTRAL_1000};
    opacity: 0.9;
  }
`;

export const FilterSearchInput = styled(TextInput)`
  width: 100%;
  border: none;
  border-bottom: 1px solid ${SA.BORDER_TERTIARY};
  background: transparent;
  font-size: 13px;
  color: ${SA.TEXT_PRIMARY};
`;

export const FacetBar = styled(Row)`
  && {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

export const FacetBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px dashed ${SA.BORDER_PRIMARY};
  background: transparent;
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
  cursor: pointer;

  &:not(:disabled):hover {
    background: ${SA.BG_SECONDARY};
    border-color: ${SA.TEXT_SECONDARY};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const FacetPopover = styled(FlexView)`
  && {
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    min-width: 180px;
    max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : '60vh')};
    overflow-y: auto;
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 100;
    animation: ${slideDown} 0.15s ease-out;
    padding: 4px 0;
  }
`;

export const AppliedChip = styled(Text)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: ${SA.GOLD_50};
    border: 1px solid ${SA.GOLD_200};
    color: ${SA.GOLD_700};
    font-size: 11px;
    white-space: nowrap;
  }
`;

export const ListScroll = styled(FlexView)`
  && {
    overflow: auto;
    flex: 1;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${SA.BORDER_PRIMARY};
    }
  }
`;

export const ListSection = styled(Row)`
  && {
    padding: 10px 16px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

const statusBorderMap = {
  OPEN: SA.INFO_300,
  ESCALATED: SA.ERROR_400,
  WAITING: SA.GOLD_500,
  IN_PROGRESS: SA.GOLD_500,
  RESOLVED: SA.SUCCESS_200,
  CLOSED: SA.SUCCESS_200,
  CLOSED_INACTIVE: SA.NEUTRAL_300,
};
const defaultStatusBorder = SA.BORDER_TERTIARY;

export const TicketCard = styled(FlexView)`
  && {
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid ${SA.BORDER_TERTIARY};
    border-left: 3px solid
      ${({ $status }) =>
        statusBorderMap[($status || '').toUpperCase()] || defaultStatusBorder};
    cursor: pointer;
    background: ${SA.BG_PRIMARY};
    animation: ${fadeInUp} 0.2s ease-out;

    &:hover {
      background: ${SA.BG_SECONDARY};
    }

    ${({ $active }) =>
      $active &&
      css`
        background: ${SA.GOLD_50};
        border-color: ${SA.GOLD_200};
        border-left-color: ${SA.GOLD_500};
      `}
  }
`;

export const TicketAvatar = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${SA.INFO_100};
  color: ${SA.INFO_700};
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

export const TicketName = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA.TEXT_PRIMARY};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
`;

export const UnreadBadge = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${SA.ERROR_600};
  display: inline-block;
  flex-shrink: 0;
`;

export const TicketSubject = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA.TEXT_SECONDARY};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
  }
`;

export const TicketFoot = styled(Row)`
  && {
    align-items: center;
    gap: 8px;
  }
`;

// Keys match BE status values (case-insensitive via lookup)
const statusColorMap = {
  OPEN: { bg: SA.INFO_50, color: SA.INFO_700, border: SA.INFO_200 },
  ESCALATED: { bg: SA.ERROR_50, color: SA.ERROR_700, border: SA.ERROR_200 },
  WAITING: { bg: SA.WARNING_100, color: SA.WARNING_700, border: SA.WARNING_300 },
  IN_PROGRESS: { bg: SA.GOLD_50, color: SA.GOLD_700, border: SA.GOLD_200 },
  RESOLVED: { bg: SA.SUCCESS_100, color: SA.SUCCESS_700, border: SA.SUCCESS_200 },
  CLOSED: { bg: SA.SUCCESS_100, color: SA.SUCCESS_700, border: SA.SUCCESS_200 },
  CLOSED_INACTIVE: { bg: SA.NEUTRAL_300, color: SA.NEUTRAL_500, border: SA.NEUTRAL_300 },
  EMAIL: { bg: SA.INFO_50, color: SA.INFO_700, border: SA.INFO_200 },
  INTERNAL: { bg: SA.GOLD_50, color: SA.GOLD_900, border: SA.GOLD_200 },
};
const defaultStatusColor = { bg: SA.GOLD_50, color: SA.GOLD_700, border: SA.GOLD_200 };

const BadgeBase = styled(Text)`
  && {
    padding: 2px 12px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
`;

export const StatusBadge = styled(BadgeBase)`
  && {
    ${({ $flex }) => $flex && `flex: 1`};
    ${({ $status, $noBorder }) => {
      const c = statusColorMap[($status || '').toUpperCase()] || defaultStatusColor;
      return `
        background: ${c.bg};
        color: ${c.color};
        border: ${$noBorder ? 'none' : `1px solid ${c.border}`};
      `;
    }}
  }
`;

const contextTagColorMap = {
  [CONTEXT_TAG_TONE.COACH]: { bg: SA.GOLD_50, color: SA.GOLD_900, border: SA.GOLD_200 },
  [CONTEXT_TAG_TONE.EVAL]: { bg: SA.INFO_50, color: SA.INFO_700, border: SA.INFO_200 },
  [CONTEXT_TAG_TONE.V4]: {
    bg: SA.NEUTRAL_1000,
    color: SA.BG_PRIMARY,
    border: SA.NEUTRAL_1000,
  },
  [CONTEXT_TAG_TONE.MIXED]: {
    bg: SA.WARNING_100,
    color: SA.WARNING_700,
    border: SA.WARNING_300,
  },
};

const defaultContextTagColor = {
  bg: SA.BG_SECONDARY,
  color: SA.NEUTRAL_500,
  border: SA.NEUTRAL_300,
};

export const ContextTag = styled(BadgeBase)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${({ $tone }) => {
    const c = contextTagColorMap[$tone] || defaultContextTagColor;
    return `
      background: ${c.bg};
      color: ${c.color};
      border: 1px solid ${c.border};
    `;
  }}
`;

export const ContextTagRow = styled(Row)`
  align-items: center;
  gap: 6px;
`;

export const StatusDisplayBox = styled(FlexView)`
  && {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    align-self: stretch;
    min-width: 0;
    gap: 8px;
    height: 36px;
    ${({ $status }) => {
      const c = statusColorMap[($status || '').toUpperCase()] || defaultStatusColor;
      return `background: ${c.bg}; color: ${c.color}; border: 1px solid ${c.border};`;
    }}
  }
`;

export const StatusEditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 14px;
  background: transparent;
  border: none;
  color: ${SA.GOLD_700};
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${SA.INFO_900};
  }
`;

export const StatusSelectorWrap = styled(FlexView)`
  && {
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    padding: 8px;
    gap: 0;
  }
`;

export const StatusOptionsWrap = styled(FlexView)`
  && {
    max-height: 320px;
    overflow-y: auto;
    gap: 0;
  }
`;

export const StatusOptionBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  background: ${({ $selected }) => ($selected ? SA.GOLD_50 : 'transparent')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  padding: 0;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const StatusCancelBtn = styled.button`
  width: 100%;
  padding: 8px;
  margin-top: 8px;
  background: transparent;
  border: 1px solid ${SA.BORDER_SECONDARY};
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const TicketTime = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA.TEXT_TERTIARY};

    &::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 4px;
      background: currentColor;
      margin-right: 6px;
      vertical-align: middle;
      border-radius: 50%;
    }
  }
`;

export const TypingIndicator = styled(Row)`
  && {
    gap: 3px;
    align-items: center;
    padding: 2px 0;
  }
`;

export const TypingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${SA.TEXT_TERTIARY};
  animation: ${typingBounce} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay || '0s'};
`;

export const ListFooter = styled(FlexView)`
  && {
    padding: 12px 16px;
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    background: ${SA.BG_SECONDARY};
    gap: 8px;
  }
`;

export const StatusBtn = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${SA.BORDER_PRIMARY};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $online }) => ($online ? SA.SUCCESS_100 : SA.BG_PRIMARY)};
  color: ${({ $online }) => ($online ? SA.SUCCESS_700 : SA.TEXT_SECONDARY)};

  &:hover {
    background: ${({ $online }) => ($online ? SA.SUCCESS_200 : SA.BG_SECONDARY)};
  }
`;

export const LoadMoreBtn = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px dashed ${SA.BORDER_PRIMARY};
  background: transparent;
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
    border-color: ${SA.TEXT_SECONDARY};
  }
`;

// ── Thread ──────────────────────────────────────────────────────────────

export const ThreadHeader = styled(Row)`
  && {
    padding: 14px 24px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
    gap: 12px;
    align-items: center;
  }
`;

export const ThreadAvatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${SA.INFO_100};
  color: ${SA.INFO_700};
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
`;

export const ThreadTitle = styled(Text)`
  && {
    font-size: 18px;
    font-weight: 700;
    color: ${SA.TEXT_PRIMARY};
    line-height: 1.2;
  }
`;

export const ThreadSubTitle = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const MsgContainer = styled(FlexView)`
  && {
    gap: 8px;
    max-width: 85%;
    animation: ${fadeInUp} 0.2s ease-out;
  }
`;

export const MsgContainerAgent = styled(MsgContainer)`
  && {
    align-self: flex-end;
  }
`;

export const MsgAvatar = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;

  ${({ $variant }) =>
    $variant === 'user' &&
    css`
      background: ${SA.INFO_100};
      color: ${SA.INFO_700};
    `}

  ${({ $variant }) =>
    $variant === 'bot' &&
    css`
      background: ${SA.NEUTRAL_1000};
      color: ${SA.GOLD_300};
    `}

  ${({ $variant }) =>
    $variant === 'agent' &&
    css`
      background: ${SA.GOLD_100};
      color: ${SA.GOLD_700};
    `}

  ${({ $variant }) =>
    $variant === 'external' &&
    css`
      background: ${SA.NEUTRAL_100};
      color: ${SA.TEXT_SECONDARY};
    `}
`;

export const MsgHead = styled(Row)`
  && {
    gap: 8px;
  }
`;

export const MsgName = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const MsgRole = styled(Text)`
  && {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 2px 6px;
    background: ${SA.BG_TERTIARY};
    color: ${SA.TEXT_SECONDARY};
  }
`;

export const MsgTime = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const MsgBodyUser = styled(FlexView)`
  && {
    padding: 12px 16px;
    background: ${SA.INFO_200};
    border: 1px solid ${SA.INFO_300};
    font-size: 14px;
    line-height: 1.5;
    color: ${SA.INFO_900};
    border-radius: ${({ $rightSide }) =>
      $rightSide ? '12px 0 12px 12px' : '0 12px 12px 12px'};
    word-break: break-word;
    width: fit-content;
    max-width: 100%;
  }
`;

export const MsgBodyBot = styled(FlexView)`
  && {
    padding: 12px 16px;
    background: ${SA.GOLD_50};
    border: 1px solid ${SA.GOLD_300};
    font-size: 14px;
    line-height: 1.5;
    color: ${SA.GOLD_900};
    border-radius: ${({ $rightSide }) =>
      $rightSide ? '12px 0 12px 12px' : '0 12px 12px 12px'};
    word-break: break-word;
    width: fit-content;
    max-width: 100%;
  }
`;

export const MsgBodyAgent = styled(FlexView)`
  && {
    padding: 12px 16px;
    background: ${SA.NEUTRAL_1000};
    border: 1px solid ${SA.NEUTRAL_1000};
    font-size: 14px;
    line-height: 1.5;
    color: ${SA.NEUTRAL_100};
    border-radius: ${({ $rightSide }) =>
      $rightSide ? '12px 0 12px 12px' : '0 12px 12px 12px'};
    word-break: break-word;
    width: fit-content;
    max-width: 100%;
  }
`;

export const EventRow = styled(FlexView)`
  && {
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 10px;
    border-top: 1px dashed ${SA.BORDER_TERTIARY};
    border-bottom: 1px dashed ${SA.BORDER_TERTIARY};
    padding: 10px 0;
  }
`;

export const EventIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px dashed ${SA.BORDER_PRIMARY};
  display: grid;
  place-items: center;
  font-size: 14px;
  color: ${SA.TEXT_TERTIARY};
`;

const systemMsgVariants = {
  info: { bg: SA.INFO_50, border: SA.INFO_300, color: SA.INFO_700 },
  error: { bg: SA.ERROR_50, border: SA.ERROR_400, color: SA.ERROR_700 },
  warning: { bg: SA.WARNING_100, border: SA.WARNING_300, color: SA.WARNING_700 },
};

export const SystemMsg = styled(Row)`
  && {
    padding: 10px 14px;
    border-left: 4px solid
      ${({ $variant }) => (systemMsgVariants[$variant] || systemMsgVariants.info).border};
    background: ${({ $variant }) =>
      (systemMsgVariants[$variant] || systemMsgVariants.info).bg};
    color: ${({ $variant }) =>
      (systemMsgVariants[$variant] || systemMsgVariants.info).color};
    font-size: 13px;
    line-height: 1.4;
    align-items: center;
    gap: 8px;
  }
`;

export const NoteCard = styled(FlexView)`
  && {
    background: ${SA.WARNING_100};
    border-left: 4px solid ${SA.GOLD_300};
    padding: 12px;
    gap: 6px;
    font-size: 13px;
    line-height: 1.4;
    color: ${SA.GOLD_900};
  }
`;

export const NoteTag = styled(Text)`
  && {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: ${SA.GOLD_700};
    padding: 2px 6px;
    background: ${SA.GOLD_100};
    align-self: flex-start;
  }
`;

// ── Trace ───────────────────────────────────────────────────────────────
export const TraceContainer = styled(FlexView)`
  && {
    border: 1px solid ${SA.BORDER_TERTIARY};
    background: ${SA.BG_PRIMARY};
    overflow: hidden;
  }
`;

export const TraceHead = styled(Row)`
  && {
    padding: 10px 14px;
    cursor: pointer;
    align-items: center;
    gap: 8px;

    &:hover {
      background: ${SA.BG_SECONDARY};
    }
  }
`;

export const TraceBadge = styled(Text)`
  && {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 2px 6px;

    ${({ $type }) =>
      $type === 'failed' &&
      css`
        background: ${SA.ERROR_100};
        color: ${SA.ERROR_700};
      `}

    ${({ $type }) =>
      $type === 'handoff' &&
      css`
        background: ${SA.WARNING_100};
        color: ${SA.WARNING_700};
      `}

    ${({ $type }) =>
      $type === 'ok' &&
      css`
        background: ${SA.SUCCESS_100};
        color: ${SA.SUCCESS_700};
      `}
  }
`;

export const TraceBody = styled(FlexView)`
  && {
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    padding: 12px 14px;
    gap: 10px;
  }
`;

export const TraceStep = styled(FlexView)`
  && {
    display: grid;
    grid-template-columns: 20px 1fr auto;
    gap: 8px;
    font-size: 13px;
    color: ${SA.TEXT_SECONDARY};
    align-items: start;
  }
`;

// ── Composer ────────────────────────────────────────────────────────────
export const ComposerWrap = styled(FlexView)`
  && {
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    padding: 16px 48px 20px;
  }
`;

export const ComposerInner = styled(FlexView)`
  && {
    max-width: 820px;
    margin: 0 auto;
    width: 100%;
    border: 1px solid ${SA.BORDER_SECONDARY};
    background: ${SA.BG_PRIMARY};

    &:focus-within {
      border-color: ${SA.GOLD_500};
    }
  }
`;

export const ComposerTabs = styled(Row)`
  && {
    gap: 2px;
    padding: 6px 6px 0;
  }
`;

export const ComposerTab = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  color: ${SA.TEXT_TERTIARY};

  ${({ $active }) =>
    $active &&
    css`
      border-bottom-color: ${SA.NEUTRAL_1000};
      color: ${SA.TEXT_PRIMARY};
    `}

  ${({ $internal }) =>
    $internal &&
    css`
      border-bottom-color: ${SA.WARNING_300};
      color: ${SA.WARNING_700};
    `}

  &:hover {
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const ComposerTextarea = styled.textarea`
  border: none;
  padding: 12px 14px;
  min-height: 44px;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  overflow: auto;
  background: ${({ internalNote }) =>
    internalNote ? COLORS.GOLD_100 : COLORS.TRANSPARENT};
  color: ${SA.TEXT_PRIMARY};
  font-family: inherit;

  &::placeholder {
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const ComposerRow = styled(Row)`
  && {
    gap: 4px;
    padding: 6px 8px;
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    align-items: center;
  }
`;

export const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${SA.TEXT_SECONDARY};

  &:hover {
    background: ${SA.BG_SECONDARY};
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const SendBtn = styled.button`
  margin-left: auto;
  padding: 6px 16px;
  background: ${SA.NEUTRAL_1000};
  color: white;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Attachment Rendering ────────────────────────────────────────────────
export const AttachImage = styled.img`
  max-width: 240px;
  max-height: 180px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
`;

export const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  max-width: 260px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

export const FileCardUser = styled(FileCard)`
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.1);
  color: ${SA.INFO_900};

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
`;

export const FileIconWrap = styled.span`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  flex-shrink: 0;
`;

export const FileIconWrapUser = styled(FileIconWrap)`
  background: rgba(0, 0, 0, 0.08);
`;

export const FileName = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 170px;
  }
`;

export const UploadingIndicator = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.TEXT_TERTIARY};
    padding: 0 14px 8px;
  }
`;

export const UploadErrorMsg = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.ERROR_600};
    padding: 0 14px 8px;
  }
`;

// ── Context Panel ───────────────────────────────────────────────────────
export const ContextContainer = styled(FlexView)`
  && {
    border-left: 1px solid ${SA.BORDER_TERTIARY};
    overflow: auto;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${SA.BORDER_PRIMARY};
    }
  }
`;

export const CtxSection = styled(FlexView)`
  && {
    padding: 16px 20px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
  }
`;

export const CtxLabel = styled(Text)`
  && {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const StudentHead = styled(Row)`
  && {
    align-items: center;
    gap: 10px;
  }
`;

export const StudentAvatar = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${SA.INFO_100};
  color: ${SA.INFO_700};
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
`;

export const KVGrid = styled.dl`
  display: grid;
  grid-template-columns: 50px 1fr;
  gap: 6px 12px;
  margin: 0;
  font-size: 13px;

  dt {
    color: ${SA.TEXT_TERTIARY};
  }

  dd {
    margin: 0;
    color: ${SA.TEXT_PRIMARY};
    word-break: break-word;
  }
`;

export const AIBanner = styled(FlexView)`
  && {
    padding: 10px 14px;
    background: ${SA.WARNING_100};
    border: 1px solid ${SA.WARNING_300};
    font-size: 13px;
    color: ${SA.WARNING_900};
    gap: 4px;
  }
`;

export const CategoryBadge = styled(Text)`
  && {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: ${SA.GOLD_50};
    border: 1px solid ${SA.GOLD_200};
    color: ${SA.GOLD_700};
    font-size: 12px;
    font-weight: 600;
  }
`;

export const CtxAction = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  background: transparent;
  font-size: 13px;
  color: ${SA.TEXT_PRIMARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const SLARow = styled(FlexView)`
  && {
    gap: 12px;
  }
`;

export const SLAItem = styled(FlexView)`
  && {
    padding: 10px 12px;
    background: ${SA.BG_SECONDARY};
    gap: 4px;
  }
`;

export const PrevTicket = styled(FlexView)`
  && {
    border-top: 1px solid ${SA.BORDER_TERTIARY};
    padding: 12px 20px;
    gap: 6px;
    cursor: pointer;

    &:hover {
      background: ${SA.BG_SECONDARY};
    }
  }
`;

export const PrevTicketStatus = styled(Text)`
  && {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 2px 6px;
    background: ${SA.BG_TERTIARY};
    color: ${SA.TEXT_SECONDARY};
  }
`;

// ── Modals ──────────────────────────────────────────────────────────────
export const ModalOverlay = styled(FlexView)`
  && {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: grid;
    place-items: center;
    z-index: 1000;
  }
`;

export const ModalCard = styled(FlexView)`
  && {
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 480px;
    width: 90%;
    max-height: 80vh;
  }
`;

export const ModalHead = styled(Row)`
  && {
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid ${SA.BORDER_TERTIARY};
    align-items: center;
  }
`;

export const ModalBody = styled(FlexView)`
  && {
    flex: 1;
    overflow: auto;
    padding: 16px 20px;
    gap: 12px;
    ${({ $minHeight }) => ($minHeight ? `min-height: ${$minHeight}px;` : '')}
  }
`;

export const ModalFoot = styled(Row)`
  && {
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid ${SA.BORDER_TERTIARY};
  }
`;

export const BtnSecondary = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${SA.BORDER_PRIMARY};
  background: ${SA.BG_PRIMARY};
  color: ${SA.TEXT_PRIMARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const BtnPrimary = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: ${SA.NEUTRAL_1000};
  color: white;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const AgentItem = styled(Row)`
  && {
    padding: 10px 12px;
    border: 1px solid ${SA.BORDER_TERTIARY};
    gap: 10px;
    align-items: center;
    cursor: pointer;

    ${({ $selected }) =>
      $selected &&
      css`
        border-color: ${SA.GOLD_500};
        background: ${SA.GOLD_50};
      `}

    &:hover {
      background: ${SA.BG_SECONDARY};
    }
  }
`;

export const AgentAvatar = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${SA.GOLD_100};
  color: ${SA.GOLD_700};
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

export const ReasonItem = styled(Row)`
  && {
    padding: 10px 12px;
    border: 1px solid ${SA.BORDER_TERTIARY};
    gap: 10px;
    align-items: center;
    cursor: pointer;

    ${({ $selected }) =>
      $selected &&
      css`
        border-color: ${SA.GOLD_500};
        background: ${SA.GOLD_50};
      `}

    &:hover {
      background: ${SA.BG_SECONDARY};
    }

    &::before {
      content: '';
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 2px solid
        ${({ $selected }) => ($selected ? SA.GOLD_500 : SA.BORDER_PRIMARY)};
      background: ${({ $selected }) => ($selected ? SA.GOLD_500 : 'transparent')};
      flex-shrink: 0;
      box-shadow: ${({ $selected }) =>
        $selected ? `inset 0 0 0 3px ${SA.BG_PRIMARY}` : 'none'};
    }
  }
`;

// ── Offline Confirmation ────────────────────────────────────────────────
export const OfflineConfirm = styled(FlexView)`
  && {
    padding: 12px 16px;
    background: ${SA.WARNING_100};
    border: 1px solid ${SA.WARNING_300};
    gap: 10px;
    font-size: 13px;
    color: ${SA.WARNING_900};
  }
`;

export const OfflineConfirmActions = styled(Row)`
  && {
    gap: 8px;
  }
`;

// ── Resolve Confirmation ────────────────────────────────────────────────
export const ResolveConfirmInline = styled(FlexView)`
  && {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 260px;
    padding: 16px;
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    z-index: 100;
    gap: 12px;
    animation: ${slideDown} 0.15s ease-out;
  }
`;

// ── Shared modal/transfer components ──────────────────────────────────
export const TransferSection = styled(FlexView)`
  && {
    gap: 8px;
  }
`;

export const TransferLabel = styled(Text)`
  && {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const AgentInfo = styled(FlexView)`
  && {
    min-width: 0;
    flex: 1;
  }
`;

export const AgentName = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const AgentSpecialty = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $online }) => ($online !== false ? SA.SUCCESS_700 : SA.NEUTRAL_500)};
`;

export const OfflineAvatar = styled(AgentAvatar)`
  background: ${SA.NEUTRAL_300};
  color: ${SA.NEUTRAL_500};
`;

export const ModalErrorMsg = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.ERROR_600};
    padding: 8px 12px;
    background: ${SA.ERROR_50};
    border: 1px solid ${SA.ERROR_200};
  }
`;

export const TransferNote = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${SA.BORDER_SECONDARY};
  background: ${SA.BG_PRIMARY};
  color: ${SA.TEXT_PRIMARY};
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${SA.GOLD_500};
  }
`;

// ── Customer Search Card ──────────────────────────────────────────────

export const CustomerCard = styled(FlexView)`
  && {
    padding: 14px 16px;
    gap: 6px;
    background: ${SA.BG_SECONDARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
  }
`;

export const CustomerName = styled(Text)`
  && {
    font-size: 15px;
    font-weight: 600;
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const CustomerMeta = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const CustomerTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  background: ${SA.GOLD_50};
  color: ${SA.GOLD_700};
  border: 1px solid ${SA.GOLD_200};
  text-transform: capitalize;
  align-self: flex-start;
`;

export const CustomerTicketHeader = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA.TEXT_SECONDARY};
    padding: 12px 0 4px;
  }
`;

// ── Category Tree ─────────────────────────────────────────────────────

export const CategoryTreeDisplayBox = styled(FlexView)`
  && {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    align-self: stretch;
    min-width: 0;
    background: ${SA.BG_SECONDARY};
    gap: 8px;
    border: 1px solid ${SA.BG_TERTIARY};
    position: relative;
    min-height: 36px;
    overflow-x: visible;
    ${({ $showingCategoryTree }) =>
      $showingCategoryTree ? 'padding: 12px 0px 12px 12px;' : 'padding-left:12px'};
  }
`;

export const CategoryTreeItemsWrap = styled(FlexView)`
  && {
    flex: 1;
    min-width: 0;
    gap: 0;
    padding-right: 45px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const CategoryTreeItem = styled(Row)`
  && {
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${SA.TEXT_PRIMARY};
    flex-wrap: nowrap;
    width: max-content;
  }
`;

export const TreeConnector = styled.span`
  color: ${SA.TEXT_TERTIARY};
  font-family: monospace;
  font-size: 12px;
  flex-shrink: 0;
`;

export const CategoryTreeLabel = styled(Text)`
  && {
    font-size: 13px;
    text-transform: capitalize;
    color: ${({ $isLast }) => ($isLast ? SA.GOLD_700 : SA.TEXT_PRIMARY)};
    font-weight: ${({ $isLast }) => ($isLast ? '600' : '400')};
    white-space: nowrap;
  }
`;

export const CategoryEditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 14px;
  background: ${SA.BG_SECONDARY};
  border: none;
  color: ${SA.GOLD_700};
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
  position: absolute;
  right: 0;
  top: 0;

  &:hover {
    background: ${SA.INFO_900};
  }
`;

export const CategoryEmptyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${SA.BORDER_SECONDARY};
  font-size: 12px;
  color: ${SA.GOLD_700};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  height: 36px;

  &:hover {
    background: ${SA.GOLD_50};
    border-color: ${SA.GOLD_300};
  }
`;

export const CategorySelectorWrap = styled(FlexView)`
  && {
    background: ${SA.BG_PRIMARY};
    border: 1px solid ${SA.BORDER_SECONDARY};
    padding: 8px;
    gap: 0;
  }
`;

export const CategoryBreadcrumb = styled(FlexView)`
  && {
    padding: 8px;
    background: ${SA.BG_SECONDARY};
    margin-bottom: 8px;
    gap: 4px;
  }
`;

export const BreadcrumbBackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: ${SA.GOLD_700};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${SA.BG_TERTIARY};
  }
`;

export const BreadcrumbPath = styled(Row)`
  && {
    align-items: center;
    flex-wrap: wrap;
    font-size: 11px;
    color: ${SA.TEXT_SECONDARY};
    padding-left: 8px;
    gap: 2px;
  }
`;

export const CategoryOptionsWrap = styled(FlexView)`
  && {
    max-height: 320px;
    overflow-y: auto;
    gap: 0;
  }
`;

export const CategoryOptionBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${({ $selected }) => ($selected ? SA.GOLD_50 : 'transparent')};
  border: none;
  font-size: 13px;
  color: ${({ $selected }) => ($selected ? SA.GOLD_700 : SA.TEXT_PRIMARY)};
  font-weight: ${({ $selected }) => ($selected ? '500' : '400')};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

export const CategoryOptionLabel = styled.span`
  flex: 1;
  text-transform: capitalize;
`;

export const CategoryCancelBtn = styled.button`
  width: 100%;
  padding: 8px;
  margin-top: 8px;
  background: transparent;
  border: 1px solid ${SA.BORDER_SECONDARY};
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
  cursor: pointer;

  &:hover {
    background: ${SA.BG_SECONDARY};
  }
`;

// ── Transfer modal: tabs ───────────────────────────────────────────────
// Per CLAUDE.md: base styled-components on @cuemath layout primitives
// (Row / Col / FlexView / Text) — not on native HTML — so theme tokens
// and typography are picked up consistently. `styled.button` is the one
// exception we keep here, mirroring the existing `AgentAvatar = styled.span`
// precedent in this file: the tab needs native button semantics
// (keyboard focus, `disabled`, screen-reader role) that the Cuemath
// `Button` component doesn't expose because it expects a `label` prop
// and is shaped for primary actions, not toggle controls.

export const TabBar = styled(Row)`
  gap: 24px;
  border-bottom: 1px solid ${SA.BORDER_TERTIARY};
  padding: 0 4px;
  margin-bottom: 12px;
`;

export const TabButton = styled.button`
  background: none;
  border: none;
  padding: 8px 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? SA.INFO_600 : SA.TEXT_SECONDARY)};
  border-bottom: 2px solid ${({ $active }) => ($active ? SA.INFO_600 : 'transparent')};
  margin-bottom: -1px;

  &:focus-visible {
    outline: 2px solid ${SA.INFO_600};
    outline-offset: 2px;
  }
`;

// ── Transfer modal: team rows ──────────────────────────────────────────
// Match the AgentItem/AgentName/AgentSpecialty pattern above — Row +
// Col + Text — so the Teams tab visually aligns with the Agents tab.

export const TeamItem = styled(Row)`
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  background: ${({ $selected }) => ($selected ? SA.INFO_50 : 'transparent')};

  &:hover {
    background: ${({ $disabled, $selected }) =>
      $disabled ? 'transparent' : $selected ? SA.INFO_50 : SA.BG_SECONDARY};
  }
`;

// span (not div) matches AgentAvatar's precedent — inline-block sizing
// without breaking the parent Row's flex flow.
export const TeamAvatar = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${SA.NEUTRAL_300};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
  margin-right: 12px;
`;

export const TeamInfo = styled(Col)`
  flex: 1;
`;

export const TeamName = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${SA.TEXT_PRIMARY};
`;

export const TeamMeta = styled(Text)`
  font-size: 12px;
  color: ${SA.TEXT_SECONDARY};
`;

// ── Shared email field components ─────────────────────────────────────

export const EmailFieldsWrap = styled(Col)`
  gap: 4px;
`;

export const EmailFieldRow = styled(Row)`
  && {
    align-items: center;
    gap: 8px;
  }
`;

export const EmailFieldLabel = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA.TEXT_TERTIARY};
    min-width: 48px;
  }
`;

export const EmailFieldInput = styled.input`
  flex: 1;
  border: none;
  border-bottom: 1px solid ${SA.BORDER_TERTIARY};
  outline: none;
  font-size: 13px;
  padding: 4px 2px;
  color: ${SA.TEXT_PRIMARY};
  background: transparent;
  font-family: inherit;

  &:focus {
    border-bottom-color: ${SA.NEUTRAL_1000};
  }

  &::placeholder {
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const BccToggle = styled.button`
  border: none;
  background: none;
  font-size: 12px;
  color: ${SA.TEXT_PRIMARY};
  cursor: pointer;
  padding: 2px 6px;

  &:hover {
    text-decoration: underline;
  }
`;

export const CcBccToggleRow = styled(Row)`
  gap: 10px;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const AttachBtn = styled(Button)`
  border-radius: 0;
  padding: ${({ $padding }) => $padding || '6px 8px'};

  & img {
    width: 14px;
    height: 14px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    background: ${SA.BG_TERTIARY};
  }
`;

export const AttachmentChip = styled(Row)`
  gap: 8px;
  background: ${SA.BG_SECONDARY};
  border: 1px solid ${SA.BORDER_TERTIARY};
  border-radius: 6px;
`;

export const AttachmentChipName = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  max-width: 200px;
`;

export const AttachmentChipRemove = styled(IconBtn)`
  width: 18px;
  height: 18px;
  background: transparent;
  color: ${SA.TEXT_TERTIARY};
  flex-shrink: 0;

  &:hover {
    background: transparent;
    color: ${SA.TEXT_PRIMARY};
  }
`;

// ── External message card (standalone email → third party) ────────────

export const ExternalMsgCard = styled(FlexView)`
  border: 1px solid ${SA.GOLD_200};
  border-left: 3px solid ${SA.GOLD_700};
  border-radius: 0;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  background: ${SA.GOLD_50};
`;

export const ExternalMsgStrip = styled(Row)`
  gap: 8px;
  background: ${SA.GOLD_100};
  border-bottom: 1px solid ${SA.GOLD_200};
`;

export const ExternalMediumLabel = styled(Row)`
  gap: 6px;
`;

export const ExternalSenderRow = styled(Row)`
  gap: 8px;
`;

export const ExternalStripRight = styled(Row)`
  gap: 8px;
`;

export const ExternalLockChip = styled(Text)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 0;
  background: ${SA.BG_TERTIARY};
  white-space: nowrap;
`;

// Deliberately NOT MsgRole (which is text-transform:uppercase) — "Sent"/"Received"
// need to render exactly as written.
export const DirectionLabel = styled(Text)`
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border-radius: 0;
  letter-spacing: 0.06em;
  background: ${SA.GOLD_100};
`;

export const ExternalHeaderGrid = styled(FlexView)`
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 3px 10px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  background: ${SA.BG_PRIMARY};
  margin: 0 14px 12px;
`;

export const ExternalHeaderValue = styled(Text)`
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const ExternalBody = styled(FlexView)`
  font-size: 13.5px;
  line-height: 1.55;
  color: ${SA.TEXT_PRIMARY};
  word-break: break-word;
`;

export const EmailComposerArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: ${SA.TEXT_PRIMARY};
  background: ${SA.BG_SECONDARY};
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${SA.NEUTRAL_1000};
  }

  &::placeholder {
    color: ${SA.TEXT_TERTIARY};
  }
`;

export const ReplyToggleBtn = styled(Button)`
  border-radius: 0;
  margin: 12px 14px 14px;
  align-self: flex-start;
  padding: 6px 14px;
  border: 1px solid ${SA.GOLD_500};

  &:hover:not([disabled]) {
    background: ${SA.GOLD_50};
  }
`;

export const ReplyFormWrap = styled(FlexView)`
  margin: 12px 14px 14px;
  gap: 8px;
  border: 1px solid ${SA.BORDER_TERTIARY};
  background: ${SA.BG_PRIMARY};
`;

// ── Recipient chips (compose modal "To") ───────────────────────────────

export const RecipientChipsWrap = styled(Row)`
  flex: 1;
  flex-wrap: wrap;
  gap: 6px;
`;

export const RecipientChip = styled(Text)`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  gap: 4px;
  padding: 3px 6px 3px 10px;
  border-radius: 12px;
  background: ${SA.BG_TERTIARY};
  border: 1px solid ${SA.BORDER_PRIMARY};
  white-space: nowrap;
`;

export const RecipientChipRemove = styled(IconBtn)`
  width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 50%;
  color: ${SA.TEXT_TERTIARY};

  &:hover {
    background: ${SA.BORDER_SECONDARY};
    color: ${SA.TEXT_PRIMARY};
  }
`;

export const RecipientInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  font-size: 13px;
  padding: 4px 0;
  color: ${SA.TEXT_PRIMARY};
  background: transparent;
  font-family: inherit;
`;

// ── Compose modal chrome ────────────────────────────────────────────────

export const ComposeInternalBanner = styled(FlexView)`
  gap: 8px;
  background: ${SA.GOLD_100};
  border-bottom: 1px solid ${SA.GOLD_200};
  color: ${SA.GOLD_700};
`;
